import type { QueryFunctionContext } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createUserCompanyPeers,
  getUserCompanyPeers,
  updateUserCompanyPeers,
  deleteUserCompanyPeers,
} from '@simplywallst/services';
import { withAuth } from '@utilities/queries';

type UserPeerCollection = Parameters<typeof createUserCompanyPeers>[0];
type UserCompanyPeerCollection = Awaited<
  ReturnType<typeof createUserCompanyPeers>
>['data']['data'][number];

export async function queryFn({
  queryKey: [_key, companyId],
}: QueryFunctionContext<[string, string]>) {
  const response = await withAuth(getUserCompanyPeers, { companyId });
  return response.data.data;
}

export function getQueryKey(companyId: string): [string, string] {
  return ['user-company-peers', companyId];
}

/**
 * Listing users peers for one company
 * @param companyId
 */
export function useUserCompanyPeers(companyId: string): {
  status: string;
  data: UserCompanyPeerCollection[];
} {
  const { data, status } = useQuery({
    queryKey: getQueryKey(companyId),
    queryFn,
    enabled: Boolean(companyId),
  });
  return {
    status,
    data: status === 'success' ? data : [],
  };
}

/**
 * upserting of user's peers
 * @param companyId
 */
export function useUpsertUserCompanyPeers(companyId: string) {
  const queryClient = useQueryClient();
  const { mutate: createUserPeers, status: creationStatus } = useMutation({
    mutationKey: ['UPSERT_USER_COMPANY_PEERS'],
    mutationFn: async ({
      userPeerCollection,
      collectionId,
    }: {
      userPeerCollection: UserPeerCollection;
      collectionId: string | null;
    }) => {
      if (collectionId !== null) {
        const response = await withAuth(updateUserCompanyPeers, {
          peerCollectionId: collectionId,
          peerCollection: userPeerCollection,
        });
        return response.data.data;
      }
      const response = await withAuth(
        createUserCompanyPeers,
        userPeerCollection
      );

      return response.data.data;
    },
    onError: (error) => {
      console.error(error);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: getQueryKey(companyId) });
    },
  });

  return {
    createUserPeers,
    creationStatus,
  };
}

export function useDeleteUserCompanyPeers(companyId: string) {
  const queryClient = useQueryClient();
  const { mutate: deleteUserPeers, status: deletionStatus } = useMutation({
    mutationKey: ['DELETE_USER_COMPANY_PEERS'],
    mutationFn: async (collectionId: string) => {
      const response = await withAuth(deleteUserCompanyPeers, {
        peerCollectionId: collectionId,
      });
      return response.data.data;
    },
    onError: (error) => {
      console.error(error);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: getQueryKey(companyId) });
    },
  });

  return {
    deleteUserPeers,
    deletionStatus,
  };
}
